import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Stack,
  Title,
  Text,
  Button,
  Group,
  TextInput,
  ActionIcon,
  Badge,
  Chip,
  ThemeIcon,
  Paper,
  NumberInput,
  UnstyledButton,
  Collapse,
  Tooltip,
  Box,
} from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconChevronDown, IconConfetti, IconX, IconPlus, IconUsers } from '@tabler/icons-react';
import { MealPlanEvent, PlannedPenciledMeal } from '../../../types/mealPlan';

type SpecialOccasionsStepProps = {
  value: {
    events?: MealPlanEvent[];
    dateRange?: [string | null, string | null];
  };
  onChange: (value: {
    events?: MealPlanEvent[];
  }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  embedded?: boolean;
  // Nights penciled in on the week-shape step (read-only here): generation
  // doesn't plan penciled days, so an event there would be silently dropped
  penciledMeals?: PlannedPenciledMeal[];
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const commonOccasions = [
  'Birthday', 'Anniversary', 'Date Night', 'Dinner Party', 'Family Gathering', 
  'Holiday', 'Celebration', 'Game Night', 'Movie Night'
];

export const SpecialOccasionsStep = ({ 
  value, 
  onChange, 
  onNext,
  onBack,
  isLastStep,
  embedded = false,
  penciledMeals,
}: SpecialOccasionsStepProps) => {
  const [selectedDay, setSelectedDay] = useState('');
  // Nearly everyone skips events, so the day picker and form stay collapsed
  // behind the header until asked for; existing events always show
  const [expanded, setExpanded] = useState(false);
  const [newOccasion, setNewOccasion] = useState('');
  const [newGuestCount, setNewGuestCount] = useState<number>(0);
  const [newSpecialRequests, setNewSpecialRequests] = useState('');
  const [showOccasionInput, setShowOccasionInput] = useState(false);

  // Calculate chronologically ordered days with dates based on start date
  const orderedDaysWithDates = useMemo(() => {
    const startDate = value.dateRange?.[0];
    if (!startDate) return DAYS_OF_WEEK.map(day => ({ day, date: '' }));

    // Parse date string manually to avoid timezone issues
    const [year, month, day] = startDate.split('-').map(Number);
    const startDateObj = new Date(year, month - 1, day);

    // For date ranges, only show days that are within the selected range
    const endDate = value.dateRange?.[1];
    if (endDate) {
      const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
      const endDateObj = new Date(endYear, endMonth - 1, endDay);
      const daysDiff = Math.ceil((endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      const orderedDays = [];
      for (let i = 0; i < daysDiff; i++) {
        const currentDate = new Date(year, month - 1, day + i);
        const dayOfWeek = currentDate.getDay();
        const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const dateStr = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`;
        orderedDays.push({
          day: DAYS_OF_WEEK[dayIndex],
          date: dateStr
        });
      }
      return orderedDays;
    }

    // If no end date, show all 7 days starting from start date
    const orderedDays = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(year, month - 1, day + i);
      const dayOfWeek = currentDate.getDay();
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const dateStr = `${currentDate.getMonth() + 1}/${currentDate.getDate()}`;
      orderedDays.push({
        day: DAYS_OF_WEEK[dayIndex],
        date: dateStr
      });
    }

    return orderedDays;
  }, [value.dateRange]);

  const orderedDays = useMemo(() => orderedDaysWithDates.map(d => d.day), [orderedDaysWithDates]);

  // Don't auto-select a day - let user choose for progressive disclosure

  const updateEvents = (updates: Partial<typeof value>) => {
    onChange({ ...value, ...updates });
  };

  // Helper functions to work with the unified event model
  const getEventForDay = useCallback((day: string): MealPlanEvent | undefined => {
    return value.events?.find(e => e.day === day);
  }, [value.events]);

  const isDayUsed = (day: string) => {
    return !!getEventForDay(day);
  };

  const penciledFor = useCallback(
    (day: string): PlannedPenciledMeal | undefined =>
      penciledMeals?.find((m) => m.day === day),
    [penciledMeals]
  );

  const selectCommonOccasion = (occasionType: string) => {
    setNewOccasion(occasionType);
  };

  const addEvent = () => {
    if (!selectedDay || penciledFor(selectedDay)) return;
    
    // Check if we have any data to save
    const hasOccasion = newOccasion.trim();
    const hasGuests = newGuestCount > 0;
    const hasRequests = newSpecialRequests.trim();
    
    if (!hasOccasion && !hasGuests && !hasRequests) {
      // If no data, remove existing event for this day
      removeEvent(selectedDay);
      return;
    }

    const currentEvents = value.events || [];
    const existingIndex = currentEvents.findIndex(e => e.day === selectedDay);
    
    // Create the new event object
    const newEvent: MealPlanEvent = {
      day: selectedDay,
      ...(hasOccasion && { occasion: newOccasion.trim() }),
      ...(hasGuests && { guestCount: newGuestCount }),
      ...(hasRequests && { specialRequests: newSpecialRequests.trim() })
    };
    
    if (existingIndex >= 0) {
      // Update existing event
      const updated = [...currentEvents];
      updated[existingIndex] = newEvent;
      updateEvents({ events: updated });
    } else {
      // Add new event
      updateEvents({ 
        events: [...currentEvents, newEvent] 
      });
    }
    
    // Reset form and move to next available day
    setNewOccasion('');
    setNewGuestCount(0);
    setNewSpecialRequests('');
    const nextDay =
      orderedDays.find(day => day !== selectedDay && !isDayUsed(day) && !penciledFor(day)) ||
      orderedDays.find(day => day !== selectedDay && !penciledFor(day)) ||
      '';
    setSelectedDay(nextDay);
  };

  const removeEvent = (day: string) => {
    updateEvents({
      events: (value.events || []).filter(e => e.day !== day)
    });
  };

  // Load existing data when day is selected
  useEffect(() => {
    if (selectedDay) {
      const existingEvent = getEventForDay(selectedDay);
      
      setNewOccasion(existingEvent?.occasion || '');
      setNewGuestCount(existingEvent?.guestCount || 0);
      setNewSpecialRequests(existingEvent?.specialRequests || '');
      setShowOccasionInput(!!existingEvent?.occasion);
    }
  }, [selectedDay, getEventForDay]);

  return (
    <Stack
      gap="xl"
      w={embedded ? '100%' : { base: '100%', sm: 700 }}
      mx="auto"
      px={embedded ? 0 : { base: 'sm', sm: 'md' }}
    >
      {/* Header */}
      {!embedded && (
        <Stack gap="md" ta="center" align="center">
          <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
            <IconUsers size={40} />
          </ThemeIcon>
          <Stack gap="xs" align="center">
            <Title order={2} size="h1" fw={700} c="dark">
              Guests & special occasions
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={400}>
              Add dinner guests or special events
            </Text>
          </Stack>
        </Stack>
      )}

      {/* Combined Event Input */}
      <Paper
        radius="lg"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="md">
          <UnstyledButton
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            style={{ width: '100%' }}
          >
            <Group gap="md" align="center" wrap="nowrap">
              <ThemeIcon size={32} radius="md" color="indigo" variant="light">
                <IconConfetti size={18} />
              </ThemeIcon>
              <div style={{ flex: 1 }}>
                <Text fw={600} size="lg" c="dark">Add event</Text>
                <Text size="sm" c="dimmed">Plan a special occasion, a dinner with friends, or both.</Text>
              </div>
              <IconChevronDown
                size={18}
                style={{
                  color: 'var(--mantine-color-gray-6)',
                  transform: expanded ? 'rotate(180deg)' : undefined,
                  transition: 'transform 150ms ease',
                  flexShrink: 0,
                }}
              />
            </Group>
          </UnstyledButton>
          
          {/* Show existing events first if any exist */}
          {value.events && value.events.length > 0 && (
            <Stack gap="sm" mb="lg">
              {orderedDaysWithDates.map(({ day, date }) => {
                const event = getEventForDay(day);

                if (!event) return null;

                return (
                  <Group key={day} justify="space-between" align="flex-start" p="sm" bg="indigo.0" style={{ borderRadius: 8 }}>
                    <Group gap="xs" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ width: '110px', display: 'flex', justifyContent: 'flex-start' }}>
                        <Badge color="indigo" variant="light">{day.slice(0, 3)} {date}</Badge>
                      </div>
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        {event.occasion && (
                          <Text size="sm" fw={500} c="dark">{event.occasion}</Text>
                        )}
                        {event.guestCount && (
                          <Text size="xs" c="indigo.7">
                            {event.guestCount} guest{event.guestCount === 1 ? '' : 's'}
                          </Text>
                        )}
                        {event.specialRequests && (
                          <Text size="xs" c="indigo.6" style={{ fontStyle: 'italic', wordBreak: 'break-word' }}>
                            "{event.specialRequests}"
                          </Text>
                        )}
                      </Stack>
                    </Group>
                    <ActionIcon 
                      variant="subtle" 
                      color="indigo" 
                      size="sm"
                      onClick={() => removeEvent(day)}
                      style={{ flexShrink: 0 }}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>
                );
              })}
            </Stack>
          )}

          {/* Day selection + form animate open together. Collapse applies
              display: none once closed, so it leaves the Stack's gap flow and
              the collapsed section carries no phantom spacing */}
          <Collapse in={expanded} transitionDuration={200}>
          <Stack gap="md">
          <Stack gap="sm">
            <Group gap="xs" wrap="wrap">
              {orderedDaysWithDates.map(({ day, date }) => {
                const isUsed = isDayUsed(day);
                // A penciled-in night can't take an event: generation doesn't
                // plan penciled days, so the event would be silently dropped
                const penciled = penciledFor(day);
                const blocked = isUsed || !!penciled;
                const chip = (
                  <Chip
                    checked={selectedDay === day}
                    onChange={() => !blocked && setSelectedDay(selectedDay === day ? '' : day)}
                    variant={blocked ? "filled" : "outline"}
                    color={selectedDay === day ? "indigo" : blocked ? "gray" : "indigo"}
                    size="md"
                    disabled={blocked}
                    style={{
                      minWidth: 'fit-content',
                      cursor: blocked ? 'not-allowed' : 'pointer'
                    }}
                    styles={{
                      label: {
                        paddingTop: 25,
                        paddingBottom: 25
                      }
                    }}
                  >
                    <Stack gap={4} align="center">
                      <Text size="xs">{day.slice(0, 3)}</Text>
                      <Text size="9px" opacity={0.5}>{date}</Text>
                    </Stack>
                  </Chip>
                );
                if (!penciled) return <Box key={day}>{chip}</Box>;
                // Disabled chips swallow pointer events, so the tooltip
                // listens on a wrapper instead
                return (
                  <Tooltip
                    key={day}
                    label={`Covered by ${penciled.name}`}
                    position="top"
                    withArrow
                    events={{ hover: true, focus: true, touch: true }}
                  >
                    <Box>{chip}</Box>
                  </Tooltip>
                );
              })}
            </Group>
          </Stack>

          {/* Event details form - Only show when day is selected */}
          {selectedDay && (
            <Stack gap="md">
              
              {/* Guest Count and Special Requests - Side by side on desktop */}
              <Group gap="md" align="flex-start" wrap="wrap">
                {/* Guest Count Section */}
                <Stack gap="sm" style={{ flex: '0 0 120px' }}>
                  <Text size="sm" fw={500} c="blue.7">
                    <IconUsers size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Guests
                  </Text>
                  <NumberInput
                    placeholder="Number of guests"
                    value={newGuestCount}
                    onChange={(value) => setNewGuestCount(typeof value === 'number' ? value : 0)}
                    min={0}
                    max={20}
                    size="sm"
                    w="100%"
                    allowNegative={false}
                  />
                </Stack>

                {/* Special Requests Section */}
                <Stack gap="sm" style={{ flex: '1 1 300px', minWidth: '250px' }}>
                  <Text size="sm" fw={500} c="gray.7">
                    Special requests (optional)
                  </Text>
                  <TextInput
                    placeholder="e.g. gluten-free, pizza night, kid-friendly..."
                    value={newSpecialRequests}
                    onChange={(e) => setNewSpecialRequests(e.target.value)}
                    size="sm"
                    maxLength={100}
                  />
                </Stack>
              </Group>

              {/* Special Occasion Section */}
              {!showOccasionInput ? (
                <Button
                  variant="subtle"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setShowOccasionInput(true)}
                  color="indigo"
                  size="sm"
                  style={{ alignSelf: 'flex-start' }}
                >
                  Add special occasion
                </Button>
              ) : (
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text size="sm" fw={500} c="indigo.7">
                      Special occasion
                    </Text>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => {
                        setShowOccasionInput(false);
                        setNewOccasion('');
                      }}
                    >
                      Remove
                    </Button>
                  </Group>
                  
                  {/* Quick select buttons */}
                  <Group gap="xs" wrap="wrap">
                    {commonOccasions.map(occasion => (
                      <Button
                        key={occasion}
                        size="xs"
                        variant={newOccasion === occasion ? "filled" : "light"}
                        color="indigo"
                        onClick={() => selectCommonOccasion(occasion)}
                        style={{ minWidth: 'fit-content' }}
                      >
                        {occasion}
                      </Button>
                    ))}
                  </Group>
                  
                  {/* Custom occasion input */}
                  <TextInput
                    placeholder="Or enter custom occasion..."
                    value={newOccasion}
                    onChange={(e) => setNewOccasion(e.target.value)}
                    size="sm"
                  />
                </Stack>
              )}
              {/* Add/Update button */}
              <Button 
                size="sm" 
                leftSection={<IconPlus size={16} />}
                onClick={addEvent}
                disabled={!selectedDay || (!newOccasion.trim() && !newGuestCount && !newSpecialRequests.trim())}
                color="orange"
              >
                {isDayUsed(selectedDay) ? 'Update event' : 'Add event'}
              </Button>
            </Stack>
          )}
          </Stack>
          </Collapse>
        </Stack>
      </Paper>



      {/* Navigation */}
      {!embedded && (
        <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
          <Button
            variant="subtle"
            size="md"
            leftSection={<IconArrowLeft size={18} />}
            onClick={onBack}
            color="orange"
          >
            Back
          </Button>

          <Button
            size="md"
            rightSection={<IconArrowRight size={18} />}
            onClick={onNext}
            color="orange"
          >
            {isLastStep ? 'Complete setup' : 'Continue'}
          </Button>
        </Group>
      )}
    </Stack>
  );
};
